if (typeof ProductPage !== 'function') {

  class ProductPage extends HTMLElement {

    constructor() {
      super();

      this.productGallery = this.querySelector('[data-js-product-gallery]');
      this.productSlider = this.querySelector('css-slider');

      this.pickupAvailabilityCompact = this.querySelector('pickup-availability-compact');
      this.pickupAvailabilityExtended = this.querySelector('pickup-availability-extended');

      this.productVariants = this.querySelector('product-variants');
      this.productForm = this.querySelector('[data-js-product-form]');
      this.addToCartButton = this.querySelector('[data-js-product-add-to-cart]');

      this.priceCompact =
        this.querySelector('[data-js-product-price-compact]') ||
        this.querySelector('[data-js-product-price]');

      this.priceExtended = this.querySelector('[data-js-product-price-extended]');

      this.firstProductGalleryIndex = null;
      this.xrButton = null;
      this.allVariants = this._getAllVariants();

      this._initGallery();
      this._initVariantLogic();
      this._initVariantPriceFallback();
      this._initCartDrawer();
      this._initModels();
      this._initBuyButtonState();
      this._initPriceElements();
    }

    _getAllVariants() {
      const jsonEl = document.querySelector('[data-sticky-pdp-variants]');
      if (!jsonEl) return [];

      try {
        const parsed = JSON.parse(jsonEl.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn('ProductPage: variants JSON could not be parsed.', error);
        return [];
      }
    }

    _formatMoney(cents) {
      if (this.productVariants && typeof this.productVariants._formatMoney === 'function') {
        return this.productVariants._formatMoney(cents, KROWN.settings.shop_money_format);
      }

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, KROWN.settings.shop_money_format);
      }

      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    _getSelectedOptions() {
      const optionSets = this.querySelectorAll('[data-js-product-variant]');
      const selected = [];

      optionSets.forEach((set) => {
        const checkedRadio = set.querySelector('input.product-variant-value:checked');
        const selectedOption = set.querySelector('select.product-variant-container');

        if (checkedRadio) {
          selected.push(checkedRadio.value);
        } else if (selectedOption) {
          selected.push(selectedOption.value);
        }
      });

      return selected;
    }

    _findVariantFromSelectedOptions() {
      if (!Array.isArray(this.allVariants) || !this.allVariants.length) return null;

      const selectedOptions = this._getSelectedOptions();
      if (!selectedOptions.length) return null;

      return this.allVariants.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        if (variantOptions.length !== selectedOptions.length) return false;

        return variantOptions.every((value, index) => String(value) === String(selectedOptions[index]));
      }) || null;
    }

    _syncVariantIdInputFromOptions() {
      const variant = this._findVariantFromSelectedOptions();
      if (!variant) return null;

      const variantInput = this.querySelector('product-form input[name="id"]');
      if (variantInput && String(variantInput.value) !== String(variant.id)) {
        variantInput.value = variant.id;
      }

      const installmentInput = this.querySelector('#product-form-installment input[name="id"]');
      if (installmentInput && String(installmentInput.value) !== String(variant.id)) {
        installmentInput.value = variant.id;
      }

      return variant;
    }

    _initGallery() {
      if (!this.productSlider || !this.productGallery) return;

      const productGaleryThumbnails = this.querySelector('.product-gallery__thumbnails-holder');
      const thumbnails = this.querySelectorAll('.product-gallery__thumbnails .thumbnail');

      if (thumbnails.length) {
        thumbnails.forEach((elm, i) => {
          if (i === 0) {
            elm.classList.add('active');
          }

          elm.addEventListener('click', e => {
            const index = e.currentTarget.dataset.index;
            const targetMedia = this.productGallery.querySelector(`.product-gallery-item[data-index="${index}"]`);
            if (!targetMedia) return;

            if (this.productSlider.sliderEnabled) {
              this.productSlider.changeSlide(index);
            } else {
              window.scrollTo({
                top: targetMedia.offsetTop + this.offsetTop,
                behavior: 'smooth'
              });
              this.thumbnailNavigationHelper(index);
            }

            this._pauseAllMedia();
            this._playMedia(targetMedia);

            if (productGaleryThumbnails) {
              productGaleryThumbnails.scrollTo({
                left: elm.offsetLeft - 50,
                behavior: 'smooth'
              });
            }
          });
        });

        this.productSlider.addEventListener('change', e => {
          this.thumbnailNavigationHelper(e.target.index);
        });
      }

      this.productSlider.addEventListener('navigation', e => {
        const media = this.productGallery.querySelector(`.product-gallery-item[data-index="${e.target.index}"]`);
        if (media) {
          this._playMedia(media);
        }
      });

      this.productSlider.addEventListener('change', e => {
        const media = this.productGallery.querySelector(`.product-gallery-item[data-index="${e.target.index}"]`);
        if (!media) return;

        this._pauseAllMedia();

        if (media.dataset.productMediaType === 'model') {
          if (this.xrButton) {
            this.xrButton.setAttribute('data-shopify-model3d-id', media.dataset.mediaId);
          }

          setTimeout(() => {
            const sliderHolder = this.productSlider.querySelector('.css-slider-holder');
            if (sliderHolder) {
              sliderHolder.classList.add('css-slider--disable-dragging');
            }
          }, 150);
        }
      });

      this.productSlider.addEventListener('ready', () => {
        if (this.firstProductGalleryIndex !== null) {
          this.productSlider.changeSlide(this.firstProductGalleryIndex, 'auto');
        }

        const productSlides = this.productSlider.querySelectorAll('.product-gallery-item');
        const productFigures = this.querySelectorAll('.apply-gallery-animation');

        this.productSlider.addEventListener('scroll', () => {
          const scrollX = -this.productSlider.element.scrollLeft;

          productSlides.forEach((slide, i) => {
            const media = productFigures[i];
            if (media) {
              media.style.transform = `translateX(${(slide.offsetLeft + scrollX) * -1 / 3}px)`;
            }
          });
        });
      });
    }

    _initVariantLogic() {
      if (!this.productVariants) return;

      this.productVariants.addEventListener('VARIANT_CHANGE', (e) => {
        const passedVariant = e?.target?.currentVariant || null;
        const optionVariant = this._syncVariantIdInputFromOptions();
        this.onVariantChangeHandler({
          target: {
            currentVariant: optionVariant || passedVariant
          }
        });
      });

      const initialVariant = this._syncVariantIdInputFromOptions();
      this.onVariantChangeHandler({
        target: {
          currentVariant: initialVariant
        }
      });
    }

    _initVariantPriceFallback() {
      const sync = () => {
        const variant = this._syncVariantIdInputFromOptions() || this._findVariantFromSelectedOptions();
        if (!variant) return;

        this.onVariantChangeHandler({
          target: {
            currentVariant: variant
          }
        });
      };

      this.addEventListener('change', (event) => {
        if (
          event.target.matches('.product-variant-value') ||
          event.target.matches('select.product-variant-container') ||
          event.target.closest('[data-js-product-variant]') ||
          event.target.closest('product-variants')
        ) {
          setTimeout(sync, 0);
          setTimeout(sync, 80);
          setTimeout(sync, 180);
        }
      });

      this.addEventListener('click', (event) => {
        if (
          event.target.closest('.product-variant__label') ||
          event.target.closest('.product-variant-value')
        ) {
          setTimeout(sync, 0);
          setTimeout(sync, 80);
          setTimeout(sync, 180);
        }
      });

      setTimeout(sync, 100);
      setTimeout(sync, 400);
      setTimeout(sync, 800);
    }

    _initCartDrawer() {
      if (document.body.classList.contains('template-cart') || KROWN.settings.cart_action !== 'overlay') return;

      let addToCartEnter = false;

      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('keyup', e => {
          if (e.keyCode === window.KEYCODES.RETURN) {
            addToCartEnter = true;
          }
        });
      }

      if (this.productForm) {
        this.productForm.addEventListener('add-to-cart', () => {
          const cartSidebar = document.getElementById('site-cart-sidebar');
          if (cartSidebar) {
            cartSidebar.show();
          }

          const cartRecommendations = document.getElementById('cart-recommendations');
          if (cartRecommendations) {
            cartRecommendations.generateRecommendations();
          }

          if (addToCartEnter) {
            setTimeout(() => {
              const closeButton = document.querySelector('#site-cart-sidebar [data-js-close]');
              if (closeButton) {
                closeButton.focus();
              }
            }, 200);
          }
        });
      }
    }

    _initModels() {
      const models = this.querySelectorAll('product-model');
      if (models.length > 0) {
        window.ProductModel.loadShopifyXR();
        this.xrButton = this.querySelector('.product-gallery__view-in-space');
      }
    }

    _initBuyButtonState() {
      if (!this.addToCartButton) return;

      const productFormElement = this.querySelector('product-form');
      if (!productFormElement) return;

      const toggleBuyState = () => {
        if (this.addToCartButton.classList.contains('disabled')) {
          productFormElement.classList.add('disable-buy-button');
        } else {
          productFormElement.classList.remove('disable-buy-button');
        }
      };

      toggleBuyState();

      const buyObserver = new MutationObserver(toggleBuyState);
      buyObserver.observe(this.addToCartButton, {
        attributes: true,
        childList: false,
        subtree: false
      });
    }

    _initPriceElements() {
      if (this.priceCompact) {
        this.priceCompact.priceOriginal = this.priceCompact.querySelector('[data-js-product-price-original]');
        this.priceCompact.priceCompare = this.priceCompact.querySelector('[data-js-product-price-compare]');
        this.priceCompact.priceUnit = this.priceCompact.querySelector('[data-js-product-price-unit]');
        this.priceCompact.priceBadge = this.priceCompact.querySelector('[data-js-product-price-badge]');
      }

      if (this.priceExtended) {
        this.priceExtended.priceOriginal = this.priceExtended.querySelector('[data-js-product-price-original]');
        this.priceExtended.priceCompare = this.priceExtended.querySelector('[data-js-product-price-compare]');
        this.priceExtended.priceUnit = this.priceExtended.querySelector('[data-js-product-price-unit]');
        this.priceExtended.priceBadge = this.priceExtended.querySelector('[data-js-product-price-badge]');
      }
    }

    _renderPriceForElement(container, variant, isExtended = false) {
      if (!container) return;

      const originalEl = container.priceOriginal || container.querySelector('[data-js-product-price-original]');
      const compareEl = container.priceCompare || container.querySelector('[data-js-product-price-compare]');
      const badgeEl = container.priceBadge || container.querySelector('[data-js-product-price-badge]');
      const unitEl = container.priceUnit || container.querySelector('[data-js-product-price-unit]');

      if (!variant) {
        if (originalEl) originalEl.innerHTML = '';
        if (compareEl) compareEl.innerHTML = '';
        if (badgeEl) badgeEl.innerHTML = '';
        if (unitEl) unitEl.innerHTML = '';
        return;
      }

      if (originalEl) {
        originalEl.innerHTML = this._formatMoney(variant.price);
      }

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (isExtended) {
            compareEl.innerHTML = `<span>${this._formatMoney(variant.compare_at_price)}</span><span>${KROWN.settings.locales.product_compare_price.replace('$SAVE_PRICE', this._formatMoney(variant.compare_at_price - variant.price))}</span>`;
          } else {
            compareEl.innerHTML = this._formatMoney(variant.compare_at_price);
          }
        } else {
          compareEl.innerHTML = '';
        }
      }

      if (badgeEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const discount = Math.round((1 - (variant.price / variant.compare_at_price)) * 100);
          badgeEl.innerHTML = `<span style="display:inline-block; padding:2px 5px; margin-left:10px; background-color:#d14545; border-radius:5px; color:white; font-weight:bold;">-${discount}%</span>`;
        } else {
          badgeEl.innerHTML = '';
        }
      }

      if (unitEl) {
        if (variant.unit_price_measurement && variant.unit_price) {
          unitEl.innerHTML = `${this._formatMoney(variant.unit_price)} / ${(variant.unit_price_measurement.reference_value != 1 ? variant.unit_price_measurement.reference_value + ' ' : '')}${variant.unit_price_measurement.reference_unit}`;
        } else {
          unitEl.innerHTML = '';
        }
      }
    }

    thumbnailNavigationHelper(index = 0) {
      this.querySelectorAll('.product-gallery__thumbnails .thumbnail').forEach((elm, i) => {
        if (String(i) === String(index)) {
          elm.classList.add('active');
        } else {
          elm.classList.remove('active');
        }
      });
    }

    onVariantChangeHandler(e) {
      const variant = e?.target?.currentVariant || this._findVariantFromSelectedOptions();

      if (variant) {
        if (variant.featured_media != null && this.productGallery) {
          const variantImg = this.productGallery.querySelector('.product-gallery-item[data-media-id="' + variant.featured_media.id + '"]');

          if (variantImg) {
            if (
              this.productGallery.classList.contains('product-gallery--slider') ||
              (this.productGallery.classList.contains('product-gallery--scroll') && window.innerWidth < 1024)
            ) {
              const slider = this.productGallery.querySelector('css-slider');

              if (slider && slider.sliderEnabled) {
                slider.changeSlide(variantImg.dataset.index);
              } else {
                this.firstProductGalleryIndex = variantImg.dataset.index;
              }
            } else {
              window.scrollTo({
                top: variantImg.offsetTop,
                behavior: 'smooth'
              });
            }
          }
        }

        if (this.pickupAvailabilityCompact && this.pickupAvailabilityCompact.classList.contains('active')) {
          this.pickupAvailabilityCompact.style.display = 'block';
          this.pickupAvailabilityCompact.fetchAvailability(variant.id);
        }

        if (this.pickupAvailabilityExtended && this.pickupAvailabilityExtended.classList.contains('active')) {
          this.pickupAvailabilityExtended.style.display = 'block';
          this.pickupAvailabilityExtended.fetchAvailability(variant.id);
        }
      } else {
        if (this.pickupAvailabilityCompact && this.pickupAvailabilityCompact.classList.contains('active')) {
          this.pickupAvailabilityCompact.style.display = 'none';
        }

        if (this.pickupAvailabilityExtended && this.pickupAvailabilityExtended.classList.contains('active')) {
          this.pickupAvailabilityExtended.style.display = 'none';
        }
      }

      this._renderPriceForElement(this.priceCompact, variant, false);
      this._renderPriceForElement(this.priceExtended, variant, true);
    }

    _pauseAllMedia() {
      document.querySelectorAll('.product-gallery .js-youtube').forEach(video => {
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });

      document.querySelectorAll('.product-gallery .js-vimeo').forEach(video => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });

      document.querySelectorAll('.product-gallery video').forEach(video => video.pause());

      document.querySelectorAll('.product-gallery product-model').forEach(model => {
        if (model.modelViewerUI) {
          model.modelViewerUI.pause();
        }
      });
    }

    _playMedia(media) {
      if (!media || !media.dataset.productMediaType) return;

      switch (media.dataset.productMediaType) {
        case 'video':
          if (media.querySelector('video')) {
            media.querySelector('video').play();
          }
          break;

        case 'external_video-youtube':
          if (media.querySelector('.js-youtube')) {
            media.querySelector('.js-youtube').contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          }
          break;

        case 'external_video-vimeo':
          if (media.querySelector('.js-vimeo')) {
            media.querySelector('.js-vimeo').contentWindow.postMessage('{"method":"play"}', '*');
          }
          break;
      }
    }
  }

  if (typeof customElements.get('product-page') === 'undefined') {
    customElements.define('product-page', ProductPage);
  }
}